import { useState, useEffect, useRef, useCallback } from 'react';
import { useKnowledgeBases, KnowledgeBase, KnowledgeBaseFile } from '../context/KnowledgeBaseContext';
import { formatErrorMessage } from '../utils/formatErrorMessage';
import { scanFilesForPhi } from '../utils/phiScan';

interface KnowledgeBaseDetailProps {
  kbId: string;
  onBack: () => void;
}

export function KnowledgeBaseDetail({ kbId, onBack }: KnowledgeBaseDetailProps) {
  const { knowledgeBases, updateKnowledgeBase, deleteKnowledgeBase, uploadFilesToKB, deleteFileFromKB } = useKnowledgeBases();
  const [kb, setKb] = useState<KnowledgeBase | null>(knowledgeBases.find(k => k.id === kbId) || null);
  const [files, setFiles] = useState<KnowledgeBaseFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(kb?.name || '');
  const [editDesc, setEditDesc] = useState(kb?.description || '');
  const [saving, setSaving] = useState(false);
  const [deletingKb, setDeletingKb] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<KnowledgeBaseFile | null>(null);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch KB detail + files from the KB detail endpoint (which includes files)
  const fetchDetail = useCallback(async (isStale?: () => boolean) => {
    setLoadingFiles(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/knowledge-bases/${kbId}`);
      if (isStale?.()) return;
      if (res.ok) {
        const data = await res.json();
        if (isStale?.()) return;
        const { files: kbFiles, ...kbData } = data;
        setKb(kbData);
        setEditName(kbData.name);
        setEditDesc(kbData.description || '');
        setFiles(kbFiles ?? []);
      } else if (res.status === 404) {
        setError('This knowledge base no longer exists. It may have been deleted.');
      } else {
        setError('Failed to load knowledge base details.');
      }
    } catch {
      if (!isStale?.()) setError('Failed to load knowledge base details.');
    } finally {
      if (!isStale?.()) setLoadingFiles(false);
    }
  }, [kbId]);

  useEffect(() => {
    let stale = false;
    fetchDetail(() => stale);
    return () => { stale = true; };
  }, [fetchDetail]);

  const handleSaveEdit = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await updateKnowledgeBase(kbId, editName.trim(), editDesc.trim() || undefined);
      setKb(prev => prev ? { ...prev, name: editName.trim(), description: editDesc.trim() || null } : prev);
      setEditing(false);
    } catch {
      setError('Failed to update knowledge base.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKB = async () => {
    if (!confirm('Delete this knowledge base and all its files? This cannot be undone.')) return;
    setDeletingKb(true);
    try {
      await deleteKnowledgeBase(kbId);
      onBack();
    } catch {
      setError('Failed to delete knowledge base.');
      setDeletingKb(false);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!confirm('Remove this file from the knowledge base?')) return;
    setDeletingFileId(fileId);
    try {
      await deleteFileFromKB(kbId, fileId);
      setFiles(prev => prev.filter(f => f.id !== fileId));
    } catch {
      setError('Failed to delete file.');
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleAddFiles = async (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const filesToUpload = Array.from(selectedFiles);
    setError(null);

    // PHI pre-scan: same acknowledgment gate as the create-KB flow.
    const phiScan = await scanFilesForPhi(filesToUpload);
    if (phiScan.hasPhiIndicators) {
      const proceed = confirm(
        `Potential patient identifiers detected in: ${phiScan.flaggedFiles.join(', ')}.\n\n` +
        'Files will be de-identified automatically before storage.\n\n' +
        'Upload anyway? (Click Cancel to stop and review the files.)'
      );
      if (!proceed) {
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
    }

    setUploading(true);
    try {
      await uploadFilesToKB(kbId, filesToUpload);
      await fetchDetail();
    } catch (err) {
      setError(formatErrorMessage(err instanceof Error ? err.message : 'Failed to upload files.'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePreview = async (file: KnowledgeBaseFile) => {
    setPreviewFile(file);
    setPreviewContent(null);
    setLoadingPreview(true);
    try {
      const res = await fetch(`/api/v1/knowledge-bases/${kbId}/files/${file.id}/content`);
      if (res.ok) {
        const data = await res.json();
        setPreviewContent(data.content);
      } else {
        setPreviewContent('Unable to load file content.');
      }
    } catch {
      setPreviewContent('Unable to load file content.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const getStatusClass = (status: KnowledgeBaseFile['status']) => {
    switch (status) {
      case 'ready': return 'text-green-500';
      case 'error': return 'text-red-400';
      default: return 'text-yellow-500';
    }
  };

  if (!kb) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-8">
        <button onClick={onBack} className="text-sm text-neutral-500 hover:text-neutral-700 flex items-center gap-1 mb-6">
          <span>← Back to Knowledge Bases</span>
        </button>
        <div className="text-center py-12 text-neutral-400">
          {error || 'Loading...'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Back button */}
      <button
        onClick={onBack}
        className="text-sm text-neutral-500 hover:text-neutral-700 flex items-center gap-1 mb-6 transition-colors"
      >
        ← Back to Knowledge Bases
      </button>

      {/* Header */}
      <div className="mb-6">
        {editing ? (
          <div className="flex flex-col gap-3">
            <input
              type="text"
              value={editName}
              onChange={e => setEditName(e.target.value)}
              className="text-2xl font-bold text-neutral-800 font-heading border-b-2 border-primary-400 focus:outline-none bg-transparent"
              disabled={saving}
              autoFocus
            />
            <textarea
              value={editDesc}
              onChange={e => setEditDesc(e.target.value)}
              placeholder="Description (optional)"
              rows={2}
              className="text-sm text-neutral-500 border border-neutral-300 rounded-btn px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              disabled={saving}
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editName.trim()}
                className="btn-primary px-4 py-1.5 text-sm font-medium disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => { setEditing(false); setEditName(kb.name); setEditDesc(kb.description || ''); }}
                disabled={saving}
                className="px-4 py-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-800"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-neutral-800 font-heading">{kb.name}</h1>
              {kb.description && (
                <p className="text-sm text-neutral-500 mt-1">{kb.description}</p>
              )}
            </div>
            <button
              onClick={() => setEditing(true)}
              className="text-xs text-neutral-400 hover:text-primary-600 transition-colors flex-shrink-0 mt-1"
            >
              Edit
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-btn text-xs text-red-700">
          {error}
        </div>
      )}

      {/* Files section */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-neutral-700">
            Files{files.length > 0 ? ` (${files.length})` : ''}
          </h2>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.txt,.csv"
              className="hidden"
              onChange={e => handleAddFiles(e.target.files)}
              aria-label="Add files"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn-primary px-3 py-1.5 text-xs font-medium disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Add Files'}
            </button>
          </div>
        </div>

        {loadingFiles ? (
          <p className="text-sm text-neutral-400 text-center py-6">Loading files...</p>
        ) : files.length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-6">
            No files yet. Click "Add Files" to upload documents.
          </p>
        ) : (
          <ul className="space-y-1">
            {files.map(file => (
              <li key={file.id} className="flex items-center justify-between py-2 px-3 bg-neutral-50 rounded-btn text-xs">
                <button
                  onClick={() => handlePreview(file)}
                  className="text-neutral-700 truncate flex-1 mr-3 text-left hover:text-primary-600 transition-colors"
                  title="Click to preview de-identified content"
                >
                  {file.filename}
                </button>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {file.deidentified && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-50 text-green-600 rounded text-[10px] font-medium"
                      title={
                        file.phi_entities_found
                          ? `${file.phi_entities_found} PHI entities found${file.phi_entity_types?.length ? `: ${file.phi_entity_types.join(', ')}` : ''}`
                          : 'De-identified — no PHI entities found'
                      }
                    >
                      De-ID'd
                      {file.phi_entities_found ? ` (${file.phi_entities_found})` : ''}
                    </span>
                  )}
                  <span className="text-neutral-400">{(file.size_bytes / 1024).toFixed(0)} KB</span>
                  {file.status === 'processing' && (
                    <svg className="w-3.5 h-3.5 animate-spin text-yellow-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  {file.status === 'ready' && (
                    <svg className={`w-3.5 h-3.5 ${getStatusClass(file.status)}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {file.status === 'error' && (
                    <span
                      className={`${getStatusClass(file.status)} font-bold`}
                      title={file.error_message ? formatErrorMessage(file.error_message) : undefined}
                    >
                      !
                    </span>
                  )}
                  <button
                    onClick={() => handleDeleteFile(file.id)}
                    disabled={deletingFileId === file.id}
                    className="text-neutral-300 hover:text-red-400 transition-colors"
                    aria-label="Delete file"
                  >
                    {deletingFileId === file.id ? (
                      <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* File preview modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-6" onClick={() => setPreviewFile(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
              <div>
                <h3 className="text-sm font-semibold text-neutral-800">{previewFile.filename}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-[10px] text-neutral-400">{(previewFile.size_bytes / 1024).toFixed(0)} KB</span>
                  {previewFile.deidentified && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-50 text-green-600 rounded text-[10px] font-medium">
                      De-identified
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => setPreviewFile(null)} className="text-neutral-400 hover:text-neutral-600 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* De-ID metadata */}
            {previewFile.deidentified && previewFile.phi_entities_found && previewFile.phi_entities_found > 0 && (
              <div className="px-5 py-3 bg-green-50 border-b border-green-100">
                <p className="text-xs text-green-700">
                  <strong>{previewFile.phi_entities_found}</strong> PHI entities replaced with surrogates:
                  {' '}{previewFile.phi_entity_types?.join(', ')}
                </p>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5">
              {loadingPreview ? (
                <p className="text-sm text-neutral-400 text-center py-8">Loading preview...</p>
              ) : previewContent ? (
                <pre className="text-xs text-neutral-700 whitespace-pre-wrap font-mono leading-relaxed">{previewContent}</pre>
              ) : (
                <p className="text-sm text-neutral-400 text-center py-8">No preview available.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Danger zone */}
      <div className="mt-8 pt-6 border-t border-neutral-200">
        <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-3">Danger Zone</h3>
        <button
          onClick={handleDeleteKB}
          disabled={deletingKb}
          className="text-sm text-red-500 hover:text-red-700 font-medium transition-colors disabled:opacity-50"
        >
          {deletingKb ? 'Deleting...' : 'Delete Knowledge Base'}
        </button>
      </div>
    </div>
  );
}
